import React, { useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Home from '../../Home';
import { clearPostViewTracker } from '../../services/postViewTracker';
import './PersistentHome.css';

const HOME_OWNER_KEY = 'bf_persistent_home_owner';

const HOME_RESTORE_KEYS = [
  'bf_home_scrollTop',
  'bf_home_fromPostId',
  'bf_home_return_source',
  'bf_home_news_article_id',
  'bf_home_from',
  'bf_discovery_scrollLeft',
  'bf_discovery_clickIndex',
];

export default function PersistentHome({
  user,
  onUserUpdate,
}) {
  const { pathname } = useLocation();

  const isHomeRoute = pathname === '/home';

  const identityKey = JSON.stringify([
    String(user?._id || ''),
    String(user?.username || '').toLowerCase(),
  ]);

  const [hasVisitedHome, setHasVisitedHome] = useState(isHomeRoute);
  const containerRef = useRef(null);

  // Remember the first visit without mounting Home on other entry routes.
  useLayoutEffect(() => {
    if (isHomeRoute) {
      setHasVisitedHome(true);
    }
  }, [isHomeRoute]);

  // Existing Home cache/scroll keys are shared.
  // Clear them when ownership changes so another identity cannot reuse them.
  useLayoutEffect(() => {
    try {
      const previousOwner = sessionStorage.getItem(HOME_OWNER_KEY);

      if (previousOwner !== identityKey) {
        HOME_RESTORE_KEYS.forEach((key) => {
          sessionStorage.removeItem(key);
        });

        localStorage.removeItem('bf_home_feed_cache_v1');
      }

      sessionStorage.setItem(HOME_OWNER_KEY, identityKey);
    } catch {}

    return () => {
      clearPostViewTracker();
    };
  }, [identityKey]);

  // Prevent keyboard focus and interaction with retained, inactive Home.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (isHomeRoute) {
      container.removeAttribute('inert');
    } else {
      container.setAttribute('inert', '');

      const focusedElement = document.activeElement;

      if (
        focusedElement instanceof HTMLElement &&
        container.contains(focusedElement)
      ) {
        focusedElement.blur();
      }
    }
  }, [isHomeRoute, hasVisitedHome]);

  if (!hasVisitedHome && !isHomeRoute) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={`bf-persistent-home ${
        isHomeRoute ? 'is-active' : 'is-inactive'
      }`}
      aria-hidden={isHomeRoute ? undefined : true}
    >
      <Home
        user={user}
        onUserUpdate={onUserUpdate}
        isActive={isHomeRoute}
      />
    </div>
  );
}
